import { useTheme } from '@cp/ds/src/hooks/useTheme';
import { useIsMobile } from '@cp/ds/src/hooks/useIsMobile';
import { Box } from '@cp/ds/src/components/box';
import { Grid } from '@cp/ds/src/components/grid';
import { StyledListItem } from '@cp/ds/src/components/styled/styledListItem';
import { StyledUnorderedList } from '@cp/ds/src/components/styled/styledUnorderedList';
import { Typography } from '@cp/ds/src/components/typography';
import { ChevronDownLightIcon } from '@cp/ds/src/icons/chevronDownLight';
import { Collapse, Container } from '@mui/material';
import React, { useState } from 'react';
import MapOutlineSvg from '../../../../assets/shapes/mapOutline.svg';
import { Circle } from './components/circle';
import { SideLine } from './components/sideLine';
import { StateList } from './components/stateList';
import { STATES } from './states';
import {
  COLUMN_WIDTH_FULL,
  REGION_NAME_WITH_BOLD_FONT,
  StateListCirclePositionIsDesktop,
  StateListCirclePositionIsMobile,
  StateListTitlePosition,
  StatesColor,
  StatesIndex,
  StatesIndexArray,
} from './const';
import { MapWithLinksRegionsProps } from './types';
import { defaultCityRenderer } from './cityRenderer';

export * from './types';

export const MapWithLinksRegions = ({ renderCity = defaultCityRenderer }: MapWithLinksRegionsProps) => {
  const isMobile = useIsMobile();
  const theme = useTheme();
  const [activeListItemId, setActiveListItemId] = useState<string | null>(null);

  return (
    <Container sx={{ position: 'relative' }}>
      <Box sx={{ position: 'relative', pb: 3 }}>
        <MapOutlineSvg style={{ width: isMobile ? '100%' : '110%', height: 'auto', marginLeft: isMobile ? 0 : '-12px' }} />
        {isMobile &&
          StatesIndexArray.map((stateIndexNumber) => (
            <Circle
              key={stateIndexNumber}
              sx={{
                ...StateListCirclePositionIsMobile[stateIndexNumber],
                backgroundColor: StatesColor[stateIndexNumber],
                height: 18,
                width: 18,
              }}
            />
          ))}
      </Box>
      <Grid columnSpacing={1} container>
        {STATES.map((state, indexState: StatesIndex) => {
          const stateListContent = (
            <StyledUnorderedList sx={{ position: 'relative' }}>
              {!isMobile && (
                <>
                  <Circle
                    key={indexState}
                    sx={{
                      ...StateListCirclePositionIsDesktop[indexState],
                      backgroundColor: StatesColor[indexState],
                      ml: '-7px',
                      height: 18,
                      width: 18,
                      zIndex: 1,
                    }}
                  />
                  <SideLine
                    sx={{
                      ...StateListCirclePositionIsDesktop[indexState],
                      backgroundColor: StatesColor[indexState],
                      height: '360px',
                    }}
                  />
                </>
              )}
              {state.regions.map((region, indexRegion) => {
                const listId = `${indexState}${indexRegion}`;
                const styleBold = REGION_NAME_WITH_BOLD_FONT.includes(region.regionName);
                const lastListItem = state.regions.length - 1 === indexRegion;
                const heightSideLine = lastListItem ? '18px' : '100%';
                const isExpandedItem = activeListItemId === listId;
                return (
                  <Box key={indexRegion} sx={{ position: 'relative' }}>
                    {!isMobile && (
                      <>
                        <SideLine sx={{ backgroundColor: StatesColor[indexState], height: heightSideLine }} />
                        <Circle
                          sx={{
                            backgroundColor: StatesColor[indexState],
                            left: '-5px',
                            top: 14,
                            height: 14,
                            width: 14,
                            zIndex: 1,
                          }}
                        />
                      </>
                    )}
                    <StyledListItem
                      onClick={() => setActiveListItemId(isExpandedItem ? null : listId)}
                      sx={{
                        cursor: 'pointer',
                        whiteSpace: 'nowrap',
                        textOverflow: 'ellipsis',
                        overflow: 'hidden',
                        textDecoration: isExpandedItem ? 'underline' : 'none',
                        maxWidth: '90%',
                        py: 1,
                      }}
                    >
                      <Typography sx={{ color: 'gray', ml: { xs: 0, md: 1.8 } }} variant="body">
                        {styleBold ? <b>{region.regionName}</b> : region.regionName}
                      </Typography>
                    </StyledListItem>
                    <ChevronDownLightIcon
                      style={{
                        position: 'absolute',
                        top: 12,
                        right: 0,
                        transform: isExpandedItem ? 'rotate(180deg)' : 'rotate(0deg)',
                        transition: 'ease-in-out 300ms transform',
                        fontSize: '1rem',
                        color: theme.palette['brand gray'],
                      }}
                    />
                    <Collapse in={isExpandedItem}>
                      <StyledUnorderedList sx={{ ml: { xs: 1, md: 3 } }}>
                        {region.citiesList.map((city) => (
                          <React.Fragment key={city.cityName}>{renderCity(city)}</React.Fragment>
                        ))}
                      </StyledUnorderedList>
                    </Collapse>
                  </Box>
                );
              })}
            </StyledUnorderedList>
          );
          const stateListTitle = (
            <>
              {isMobile && (
                <>
                  <SideLine
                    sx={{
                      backgroundColor: StatesColor[indexState],
                      height: 5,
                      width: 25,
                      borderRadius: `${theme.borderRadius.small}px`,
                    }}
                  />
                  <Circle
                    sx={{
                      backgroundColor: StatesColor[indexState],
                      height: 15,
                      width: 15,
                      left: 5,
                    }}
                  />
                </>
              )}
              <Typography
                sx={{
                  position: { xs: 'relative', md: 'absolute' },
                  ...StateListTitlePosition[indexState],
                  color: StatesColor[indexState],
                  m: 0,
                  ml: { xs: 4.5, md: 0 },
                }}
                variant="h6"
              >
                {state.stateName}
              </Typography>
            </>
          );

          return (
            <Grid key={indexState} size={{ md: COLUMN_WIDTH_FULL / STATES.length, xs: COLUMN_WIDTH_FULL }}>
              <StateList content={stateListContent} title={stateListTitle} />
            </Grid>
          );
        })}
      </Grid>
    </Container>
  );
};
